/**
 * Run-scoped refresh/recalc tools for the multi-hop executor agent.
 *
 * These tools let the agent trigger a FactSet data refresh and/or Excel
 * recalculation mid-run, so it can verify formula values after writing
 * FactSet formulas instead of settling for #Calc/#NAME? warnings.
 *
 * The host callbacks are injected via `ExecutorToolRuntimeContext` so
 * the tool layer stays UI-agnostic; the actual host-ready gating and
 * pinned workbook reactivation live in ExcelApp.tsx.
 */

import type { ChartCapabilityToolDef } from "./chartCapabilityTools";
import type { ExecutorToolRuntimeContext } from "./executorToolRuntime";

// ---------------------------------------------------------------------------
// Tool definitions
// ---------------------------------------------------------------------------

export const REFRESH_FACTSET_DATA_TOOL: ChartCapabilityToolDef = {
  name: "refresh_factset_data",
  description:
    "Run ONE consolidated refresh of FactSet data after all FactSet formulas needed for " +
    "the current work have been written, or when the user explicitly asks to " +
    "refresh existing FactSet formulas. Include all relevant worksheets in a " +
    "single call; do not refresh each worksheet separately. Choose scope " +
    "'worksheets' with the exact sheet names when only specific sheets need " +
    "refreshing, or scope 'workbook' when every worksheet must be refreshed. " +
    "After this tool succeeds, call recalculate_workbook with mode 'full', then " +
    "read the affected ranges to verify their resolved values. Do not call this " +
    "tool repeatedly merely because a read still shows an unresolved value.",
  strict: true,
  inputSchema: {
    type: "object",
    properties: {
      scope: {
        type: "string",
        enum: ["workbook", "worksheets"],
        description:
          "Use 'workbook' to refresh every worksheet in the pinned workbook. " +
          "Use 'worksheets' to refresh only the exact names supplied in sheetNames.",
      },
      sheetNames: {
        anyOf: [
          { type: "array", items: { type: "string" }, minItems: 1 },
          { type: "null" },
        ],
        description:
          "For scope 'worksheets', provide the non-empty list of exact worksheet " +
          "names to refresh. For scope 'workbook', this must be null.",
      },
    },
    required: ["scope", "sheetNames"],
    additionalProperties: false,
  },
};

export const RECALCULATE_WORKBOOK_TOOL: ChartCapabilityToolDef = {
  name: "recalculate_workbook",
  description:
    "Recalculate Excel after refresh_factset_data succeeds, or after writing " +
    "ordinary formulas whose dependents must update. Use 'full' after a FactSet " +
    "refresh, then read the affected ranges to verify the resulting values. Use " +
    "'recalculate' only for a normal changed-cell calculation. fullRebuild is an " +
    "exception for rebuilding Excel's dependency graph, not the routine FactSet path.",
  strict: true,
  inputSchema: {
    type: "object",
    properties: {
      mode: {
        type: "string",
        enum: ["recalculate", "full", "fullRebuild"],
        description:
          "Calculation strength: use 'full' after FactSet refresh; use " +
          "'recalculate' for normal changed cells; reserve 'fullRebuild' for " +
          "an exceptional dependency-graph rebuild.",
      },
    },
    required: ["mode"],
    additionalProperties: false,
  },
};

export const EXECUTOR_REFRESH_TOOLS: readonly ChartCapabilityToolDef[] = [
  REFRESH_FACTSET_DATA_TOOL,
  RECALCULATE_WORKBOOK_TOOL,
];

// ---------------------------------------------------------------------------
// Callback types (injected from host layer)
// ---------------------------------------------------------------------------

export interface RefreshFactSetDataOptions {
  sheetNames?: string[];
  refreshAll?: boolean;
  pinnedWorkbookName?: string;
  fallbackSheet?: string;
}

export interface RefreshFactSetDataResult {
  ok: boolean;
  refreshed: boolean;
  scope: "sheets" | "all" | "active";
  sheetNames?: string[];
  /** Exact worksheets the resolved host call explicitly included. */
  refreshedSheetNames: string[];
  /** Requested worksheets whose individual host call rejected or timed out. */
  failedSheetNames?: string[];
  /** Per-sheet host acknowledgements retained for diagnostics. */
  worksheetResults?: Array<{
    sheetName: string;
    ok: boolean;
    message: string;
    error?: string;
  }>;
  /** False when the host fell back to an implicit active-sheet refresh. */
  scopeConfirmed: boolean;
  elapsedMs: number;
  message: string;
  error?: string;
}

export type ExecutorRefreshFactSetData = (
  opts: RefreshFactSetDataOptions,
) => Promise<RefreshFactSetDataResult>;

export interface RecalculateWorkbookOptions {
  mode?: "recalculate" | "full" | "fullRebuild";
  pinnedWorkbookName?: string;
  fallbackSheet?: string;
}

export interface RecalculateWorkbookResult {
  ok: boolean;
  recalculated: boolean;
  mode: string;
  elapsedMs: number;
  message: string;
  error?: string;
}

export type ExecutorRecalculateWorkbook = (
  opts: RecalculateWorkbookOptions,
) => Promise<RecalculateWorkbookResult>;

// ---------------------------------------------------------------------------
// Executor functions
// ---------------------------------------------------------------------------

interface RefreshInput {
  stepId?: string;
  scope?: "workbook" | "worksheets";
  sheetNames?: string[] | null;
  /** Legacy dispatcher compatibility; absent from the model-facing schema. */
  refreshAll?: boolean;
}

type ParsedRefreshInput =
  | {
      ok: true;
      refreshAll: boolean;
      sheetNames?: string[];
    }
  | {
      ok: false;
      error: string;
    };

function parseRefreshInput(rawInput: unknown): ParsedRefreshInput {
  const input =
    rawInput !== null && typeof rawInput === "object" && !Array.isArray(rawInput)
      ? (rawInput as RefreshInput)
      : {};

  if (input.scope !== undefined) {
    if (input.scope === "workbook") {
      if (input.sheetNames !== null) {
        return {
          ok: false,
          error: "scope 'workbook' requires sheetNames to be null.",
        };
      }
      return { ok: true, refreshAll: true };
    }
    if (input.scope === "worksheets") {
      if (
        !Array.isArray(input.sheetNames) ||
        input.sheetNames.length === 0 ||
        input.sheetNames.some(
          (name) => typeof name !== "string" || name.trim().length === 0,
        )
      ) {
        return {
          ok: false,
          error:
            "scope 'worksheets' requires a non-empty sheetNames array of exact worksheet names.",
        };
      }
      return {
        ok: true,
        refreshAll: false,
        sheetNames: input.sheetNames,
      };
    }
    return {
      ok: false,
      error: "Invalid refresh scope. Expected 'workbook' or 'worksheets'.",
    };
  }

  // Compatibility for old harnesses, paused calls, and internal callers. These
  // aliases are deliberately absent from the strict provider schema.
  if (input.refreshAll === true) {
    return { ok: true, refreshAll: true };
  }
  const legacySheetNames =
    Array.isArray(input.sheetNames) &&
    input.sheetNames.length > 0 &&
    input.sheetNames.every(
      (name) => typeof name === "string" && name.trim().length > 0,
    )
      ? input.sheetNames
      : undefined;
  return {
    ok: true,
    refreshAll: false,
    ...(legacySheetNames ? { sheetNames: legacySheetNames } : {}),
  };
}

export async function executeRefreshFactSetDataTool(
  ctx: ExecutorToolRuntimeContext,
  rawInput: unknown,
): Promise<string> {
  const input = parseRefreshInput(rawInput);
  if (input.ok === false) {
    return JSON.stringify({
      ok: false,
      refreshed: false,
      scope: "none",
      refreshedSheetNames: [],
      scopeConfirmed: false,
      elapsedMs: 0,
      message: "Invalid refresh_factset_data arguments.",
      error: input.error,
    });
  }
  const { refreshAll, sheetNames } = input;

  if (!ctx.refreshFactSetData) {
    return JSON.stringify({
      ok: false,
      refreshed: false,
      scope: "none",
      refreshedSheetNames: [],
      scopeConfirmed: false,
      elapsedMs: 0,
      message: "refresh_factset_data callback not available in this context.",
      error: "not_available",
    });
  }

  const result = await ctx.refreshFactSetData({
    sheetNames,
    refreshAll,
    pinnedWorkbookName: ctx.session.pinnedWorkbookName,
    fallbackSheet: sheetNames?.[0] ?? ctx.session.sessionLockSheet,
  });

  if (result.ok && result.refreshed && result.scopeConfirmed) {
    const refreshedNames = new Set(
      result.refreshedSheetNames.map((name) => name.trim().toLowerCase()),
    );
    for (const pendingName of Array.from(
      ctx.accumulators.fdsRefreshSheets,
    )) {
      if (refreshedNames.has(pendingName.trim().toLowerCase())) {
        ctx.accumulators.fdsRefreshSheets.delete(pendingName);
      }
    }
    if (refreshAll && result.scope === "all") {
      ctx.accumulators.deferredRefreshAllRequested = false;
    }
  }

  return JSON.stringify(result);
}

interface RecalcInput {
  stepId?: string;
  mode?: "recalculate" | "full" | "fullRebuild";
}

const RECALC_MODE_STRENGTH = {
  recalculate: 0,
  full: 1,
  fullRebuild: 2,
} as const;

type RecalcMode = keyof typeof RECALC_MODE_STRENGTH;

function isRecalcMode(value: unknown): value is RecalcMode {
  return typeof value === "string" && value in RECALC_MODE_STRENGTH;
}

function satisfiesDeferredRecalc(completed: RecalcMode, deferred: RecalcMode): boolean {
  return RECALC_MODE_STRENGTH[completed] >= RECALC_MODE_STRENGTH[deferred];
}

export async function executeRecalculateWorkbookTool(
  ctx: ExecutorToolRuntimeContext,
  rawInput: unknown,
): Promise<string> {
  const input = (rawInput ?? {}) as RecalcInput;
  if (input.mode !== undefined && !isRecalcMode(input.mode)) {
    return JSON.stringify({
      ok: false,
      recalculated: false,
      mode: input.mode,
      elapsedMs: 0,
      message: "Invalid recalculate_workbook arguments.",
      error:
        "Invalid mode. Expected 'recalculate', 'full', or 'fullRebuild'.",
    });
  }
  const mode = input.mode ?? "full";

  if (!ctx.recalculateWorkbook) {
    return JSON.stringify({
      ok: false,
      recalculated: false,
      mode,
      elapsedMs: 0,
      message: "recalculate_workbook callback not available in this context.",
      error: "not_available",
    });
  }

  const result = await ctx.recalculateWorkbook({
    mode,
    pinnedWorkbookName: ctx.session.pinnedWorkbookName,
    fallbackSheet: ctx.session.sessionLockSheet,
  });

  if (result.ok && result.recalculated) {
    const completedMode = isRecalcMode(result.mode) ? result.mode : mode;
    const deferredMode = ctx.accumulators.deferredRecalcMode;
    if (deferredMode && satisfiesDeferredRecalc(completedMode, deferredMode)) {
      ctx.accumulators.deferredRecalcMode = null;
    }
  }

  return JSON.stringify(result);
}
